import { StarIcon as OutlineStarIcon } from "@heroicons/react/24/outline";
import { StarIcon as SolidStarIcon } from "@heroicons/react/20/solid";
import { Modal } from "components/layouts";
import moment from "moment";
import { FC } from "react";
import { useSearch } from "react-location";
import { closeModal, dispatchModal, wrapClick } from "utils";
import type { Complaint } from "./schema";

type FeedbackContainerProps = {
  complaint: Complaint;
};

const MAX_STARS = 5;

function FeedbackStars({ rating }: { rating: number }) {
  const filled = Math.max(0, Math.min(MAX_STARS, Math.round(rating)));

  return (
    <div className="flex gap-1 items-center">
      {new Array(filled).fill(0).map((_, i) => (
        <SolidStarIcon key={`solid-${i}`} className="h-5 w-5 text-yellow-500" />
      ))}
      {new Array(MAX_STARS - filled).fill(0).map((_, i) => (
        <OutlineStarIcon
          key={`outline-${i}`}
          className="h-5 w-5 text-yellow-500"
        />
      ))}
    </div>
  );
}

const FeedbackContainer: FC<FeedbackContainerProps> = ({ complaint }) => {
  const searchParams = useSearch();
  const feedback = complaint?.feedback;
  const feedbackSubmitted = feedback?.status === "Submitted";

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6 flex gap-4 ">
        <div className={"flex-1"}>
          <h3 className="text-lg font-medium text-gray-900">
            Customer Feedback
          </h3>
          {feedbackSubmitted ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm font-medium text-gray-700 mt-1">
                Feedback Submitted
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-sm font-medium text-gray-700 mt-1">
                Feedback is pending
              </p>
            </div>
          )}
        </div>
        <div className={"flex-shrink-0 grid place-items-center"}>
          <button
            type="button"
            className="w-full disabled:cursor-not-allowed disabled:bg-primary-600/60 disabled:hover:bg-primary-600/50 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            disabled={!feedbackSubmitted}
            onClick={wrapClick(dispatchModal(complaint._id, "view-feedback"))}
          >
            View Feedback
          </button>
        </div>
      </div>
      <Modal
        open={searchParams.modal === "view-feedback"}
        setOpen={(val) => {
          if (val) {
            dispatchModal(complaint._id, "view-feedback");
          } else {
            closeModal();
          }
        }}
        title="Feedback Information"
        description="Details of customer feedback are shown below"
      >
        {feedbackSubmitted && feedback && (
          <div className="space-y-6 divide-y divide-gray-200">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Status
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {feedback.status}
                </div>
              </div>
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Submitted At
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {feedback.submittedAt
                    ? moment(feedback.submittedAt).format("DD/MM/YYYY hh:mm A")
                    : "N/A"}
                </div>
              </div>
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Issue Status
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {feedback.serviceStatus === "Resolved"
                    ? "Resolved"
                    : feedback.serviceStatus === "Unresolved"
                      ? "Not resolved"
                      : "N/A"}
                </div>
              </div>
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Service Rating
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  {typeof feedback.serviceRating === "number" ? (
                    <FeedbackStars rating={feedback.serviceRating} />
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <span className="block text-sm font-light text-gray-700">
                  Additional Comments
                </span>
                <div className="mt-1 block w-full sm:text-sm whitespace-pre-wrap">
                  {feedback.serviceFeedback || "N/A"}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FeedbackContainer;
